/**
 * Reading a field off a record, the way a generated app's wire shape needs it.
 *
 * Records from `/api/<entity>` arrive with linked records POPULATED —
 * `status: { _id, name: 'Open' }` — and widget configs name the part they
 * want with a dotted path: `status.name`. Until components read records
 * straight from a data source, the host flattened every dotted path it
 * needed onto the row first; this does that lookup in place instead.
 *
 * A literal key wins over traversal: a `.mapRow` that flattens refs (the
 * generated apps' `_flattenRefsForTable`) writes a real `"status.name"` key
 * holding the display value, and that value is what the cell should show.
 */
export function getField(record, path) {
  if (record == null || path == null) return undefined;
  if (Object.prototype.hasOwnProperty.call(record, path)) return record[path];
  if (typeof path !== 'string' || !path.includes('.')) return record[path];
  let cur = record;
  for (const part of path.split('.')) {
    if (cur == null) return undefined;
    // A multi-ref (`owners: [{ name }, …]`) read through a dotted path yields
    // each element's value — the same list the cell shows as chips.
    cur = Array.isArray(cur) ? cur.map((x) => (x == null ? x : x[part])) : cur[part];
  }
  return cur;
}

/**
 * The text a populated ref stands for. `displayField` first, then the keys a
 * generated record uses for its name. Scalars pass through.
 */
export function refLabel(v, displayField) {
  if (v == null || typeof v !== 'object' || v instanceof Date) return v;
  for (const k of [displayField, 'name', 'title', 'label']) {
    if (k && v[k] != null && v[k] !== '') return v[k];
  }
  return undefined;
}
